/**
 * 0.37.99 — Page (form structure) cache with localStorage
 * persistence + stale-while-revalidate.
 *
 * Solves the second half of the ~5s time-to-label admin reported:
 * StaticPage's useEffect fires `getPage(slug, language)` after
 * first render, and while the network round-trip is pending the
 * user sees "Loading…". For repeat visits to the same page, this
 * cache serves the previously-received structure synchronously,
 * so the first render already has the data; a fresh fetch fires
 * in the background and silently updates the UI if anything
 * changed (admin edited the form since last visit).
 *
 * Mirrors the libraryCache.js pattern (0.37.91). Same debug
 * flag (`?libdebug=1` or `window.__DEBUG_LIBS = true`) turns on
 * event logging.
 *
 * Design notes
 * ------------
 * Cache key: `<slug>|<language>`. Different languages get
 * different entries because the backend serves translated
 * content in getPage(slug, lang).
 *
 * localStorage prefix: `gt18:page:v1:`. Versioned so any future
 * schema change is a clean cache wipe by bumping v1 → v2.
 *
 * Payload shape is whatever getPage returns (a page object with
 * title, structure, page_type, etc.). We JSON-round-trip so it
 * has to be JSON-serializable; today it is.
 *
 * On failure of the fresh fetch, the stale cached copy is
 * preserved so the UI keeps showing SOMETHING rather than
 * flipping to an error state.
 */

import { appPath } from './basePath';
import { getPage as getPageNetwork } from '../api/pages';

const STORAGE_PREFIX = 'gt18:page:v1:';

// 0.37.100 — Optional callback fired whenever a page structure
// is resolved (from network or from cache hydrate). Set once by
// libraryCache.js at import time so pageCache can kick off
// library batches for `=lib(...)` references AS SOON AS the
// structure is available, without waiting for React to render
// and PageContentRenderer's prewarm useEffect to fire. This
// overlaps library loading with React's mount+render work.
let _onStructureResolved = null;
export function setOnStructureResolved(fn) { _onStructureResolved = fn; }

// In-memory cache: `<slug>|<lang>` → page object.
const _cache = new Map();
// In-flight fetches: same key → Promise. Dedupes concurrent
// requests for the same page.
const _inflight = new Map();
// Which keys have been network-refreshed this session. First
// call per session always hits the network for freshness;
// subsequent calls in the same session return cached without
// another round-trip.
const _refreshedThisSession = new Set();

// Debug logging — same opt-in as libraryCache.js so both modules
// share the `?libdebug=1` toggle.
let _DEBUG = false;
try {
  if (typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search || '');
    if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) _DEBUG = true;
  }
} catch (_e) { /* silent */ }
function _log(msg, ...args) {
  if (!_DEBUG) return;
  const t = typeof performance !== 'undefined' && performance.now
    ? Math.round(performance.now()) : Date.now();
  // eslint-disable-next-line no-console
  console.log(`[pagecache t=${t}ms] ${msg}`, ...args);
}

function _key(slug, lang) { return `${slug}|${lang || ''}`; }

function _loadFromStorage(k) {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_PREFIX + k);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) { return null; }
}

function _saveToStorage(k, payload) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_PREFIX + k, JSON.stringify(payload));
  } catch (e) {
    _log(`_saveToStorage FAILED ${k}: ${e && e.name}: ${e && e.message}`);
  }
}

function _hydrateFromStorage() {
  let count = 0;
  try {
    if (typeof localStorage === 'undefined') { _log('hydrate skipped — no localStorage'); return; }
    for (let i = 0; i < localStorage.length; i++) {
      const sk = localStorage.key(i);
      if (!sk || !sk.startsWith(STORAGE_PREFIX)) continue;
      const k = sk.slice(STORAGE_PREFIX.length);
      const payload = _loadFromStorage(k);
      if (payload) { _cache.set(k, payload); count++; }
    }
  } catch (_e) { /* silent */ }
  _log(`hydrate done — ${count} pages from localStorage`);
}
_hydrateFromStorage();

/* 0.38.02 — Preflight: as soon as this module loads (before React
 * even boots), inspect the URL. If it looks like a public form
 * page (`/{lang}/p/{slug}`), fire fetchPage immediately. This
 * overlaps the network round-trip with React's boot + provider
 * setup + route matching (~1.5s combined). By the time StaticPage
 * mounts and calls fetchPage, the response is either already
 * cached in memory or the promise is inflight and ready to
 * resolve.
 *
 * On repeat visits with a cached page, this triggers the
 * stale-while-revalidate refresh at t=~1000ms instead of the
 * ~t=2650ms observed in 0.38.01 (after React boot). On first
 * visits, the fetch fires ~1.5s earlier than before.
 *
 * Guards: gracefully no-op if URL doesn't match, if window
 * is unavailable (SSR/tests), or if any parsing error occurs.
 */
try {
  if (typeof window !== 'undefined' && window.location) {
    const path = appPath() || '';
    // Match /{lang}/p/{slug} where lang is 2 letters and slug
    // is anything up to the next / or query/hash boundary.
    const m = path.match(/^\/([a-z]{2})\/p\/([^/?#]+)/i);
    if (m) {
      const [, urlLang, urlSlug] = m;
      _log(`preflight START ${urlSlug}|${urlLang}`);
      // Fire and forget — the promise gets cached via _inflight
      // and StaticPage's later fetchPage() call joins it.
      fetchPage(urlSlug, urlLang).catch(() => { /* silent — StaticPage handles */ });
    }
  }
} catch (_e) { /* preflight is best-effort; never break app boot */ }


/**
 * Synchronous cache read. Returns the cached page object or null.
 * Callers use this to render the initial UI immediately, then
 * separately call `getPageStale()` (below) to trigger the
 * background refresh.
 */
/* 0.49.12 - Whether the stored copy has already been checked with the
   server in this visit (so it is the page as it is now). */
export function isPageFresh(slug, lang) {
  return _refreshedThisSession.has(_key(slug, lang));
}

export function getCachedPage(slug, lang) {
  const k = _key(slug, lang);
  return _cache.get(k) || null;
}


/**
 * Stale-while-revalidate fetch. On first call per session for a
 * given (slug, lang), always hits the network. On subsequent
 * calls, returns cached immediately.
 *
 * Returns a Promise that resolves to the freshest page object
 * (from network on first call, from cache thereafter).
 *
 * On network failure with a cached copy present, resolves to
 * the cached copy (UI keeps working). On failure with no cache,
 * rejects.
 */
export function fetchPage(slug, lang) {
  const k = _key(slug, lang);
  const inflight = _inflight.get(k);
  if (inflight) {
    _log(`fetchPage REUSING inflight promise ${k}`);
    return inflight;
  }
  const cached = _cache.get(k);
  if (cached && _refreshedThisSession.has(k)) {
    _log(`fetchPage CACHE HIT (already refreshed) ${k}`);
    return Promise.resolve(cached);
  }
  const cacheState = cached ? 'populated (stale, refreshing)' : 'empty';
  _log(`fetchPage START ${k} cache=${cacheState}`);
  const t0 = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  const promise = getPageNetwork(slug, lang || 'en')
    .then((data) => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - t0);
      _inflight.delete(k);
      // 0.46.99 - A page that requires login is never kept: a copy would
      // show it to the next visitor of this browser before the server
      // said no.
      if (data && data.settings && data.settings.requires_login) {
        _cache.delete(k);
        return data;
      }
      _cache.set(k, data);
      _refreshedThisSession.add(k);
      _saveToStorage(k, data);
      _log(`fetchPage RESOLVED ${k} took=${took}ms`);
      // 0.37.100 — Kick off library preloads as soon as the
      // structure arrives, in parallel with StaticPage's
      // setState + React re-render. By the time PageContentRenderer
      // mounts, batches are already in flight.
      if (_onStructureResolved) {
        try { _onStructureResolved(data, lang); }
        catch (_e) { /* ignore preload errors — libraries still resolve on-demand */ }
      }
      return data;
    })
    .catch((err) => {
      _inflight.delete(k);
      _log(`fetchPage FAILED ${k}: ${err && err.message}`);
      /* 0.47.24 - The page now needs a signed-in visitor: its stored
         copy must go, in every language, and the refusal must reach
         the page (which goes to the login page). Before, a visitor
         who had opened the page before "Requires login" was set was
         shown the stored copy, as for a network failure. */
      if (err && err.status === 401 && (err.message === 'login_required'
          || (err.body && err.body.detail === 'login_required'))) {
        forgetPage(slug);
        throw err;
      }
      // If we have a stale copy, hand that back rather than
      // failing the render — user still sees content.
      if (cached) {
        _log(`fetchPage falling back to stale cache ${k}`);
        return cached;
      }
      throw err;
    });
  _inflight.set(k, promise);
  return promise;
}


/* 0.47.24 - Forget every stored copy of a page (all languages), in
 * memory and in localStorage. */
export function forgetPage(slug) {
  const prefix = `${slug}|`;
  for (const k of Array.from(_cache.keys())) {
    if (k.startsWith(prefix)) { _cache.delete(k); _refreshedThisSession.delete(k); }
  }
  try {
    if (typeof localStorage === 'undefined') return;
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const sk = localStorage.key(i);
      if (sk && sk.startsWith(STORAGE_PREFIX + prefix)) doomed.push(sk);
    }
    doomed.forEach(sk => localStorage.removeItem(sk));
  } catch (_e) { /* silent */ }
}
